import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { uniqBy } from 'lodash';
import { Helmet } from 'react-helmet';

import { withStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';

import Grid from '@material-ui/core/Grid';
import Toolbar from '@material-ui/core/Toolbar';

import {
  getFormValues,
  getFormError,
  isSubmitting,
  hasSubmitSucceeded,
  submit,
  reset,
  change,
  arrayPush,
  arrayRemove,
  clearSubmitErrors,
  isPristine,
} from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Prompt from 'components/common/Prompt';
import PatientDetailsBar from 'components/patient/PatientDetailsBar';
import InventorySearch from 'components/inventory/InventorySearch';
import OrderCreateEditForm from 'components/workflow/OrderCreateEditForm';
import OrdersActionsBar from 'components/workflow/OrdersActionsBar';
import VendorCreateEditDialog from 'components/vendor/VendorCreateEditDialog';
import InventoryCategoryCreateEditDialog from 'components/settings/InventoryCategoryCreateEditDialog';
import InventoryConditionCreateEditDialog from 'components/settings/InventoryConditionCreateEditDialog';
import InventoryWarehouseCreateEditDialog from 'components/settings/InventoryWarehouseCreateEditDialog';
import InventoryCreateEditDialog from 'components/inventory/InventoryCreateEditDialog';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';

import { fetchPatient } from 'redux/modules/patients';
import {
  fetchOrder,
  clearOrders,
} from 'redux/modules/orders';
import { fetchArchiveOrder } from 'redux/modules/archiveOrders';
import {
  clearInventories,
  fetchInventories,
} from 'redux/modules/inventory';
import {
  clearConnectedOrders,
} from 'redux/modules/connectedOrders';
import { fetchFacilities } from 'redux/modules/facilities';
import { fetchBusiness } from 'redux/modules/business';
import { fetchSystemSettings } from 'redux/modules/systemSettings';
import { clearRecoverOrder } from 'redux/modules/recoverOrder';
import { addNotification } from 'redux/modules/notifications';

import throttle from 'utils/throttle';
import objectDiff from 'utils/objectDiff';
import formatPrice from 'utils/formatPrice';
import formatPrescriberName from 'utils/formatPrescriberName';
import sortInsurance from 'utils/sortInsurance';
import formatOrderNumber from 'utils/formatOrderNumber';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import dateFormat from 'config/dateFormat';
import timeFormat from 'config/timeFormat';
import timeDateFormat from 'config/timeDateFormat';

const styles = theme => ({
  gutterBottom: {
    height: 72,
  },
  toolbar: {
    marginTop: 66,
    marginBottom: 8,
    [theme.breakpoints.down('sm')]: {
      marginTop: 99,
      zIndex: 1100,
    },
  },
});

class OrderCreateEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      offset: 0,
      renderOrderLine: props.initialValues && props.initialValues.orderLine ? [props.initialValues.orderLine[0]] : [],
      triggerLoading: true,
      inventoryCreateDialogOpen: false,
      vendorCreateDialogOpen: false,
      warehouseCreateDialogOpen: false,
      conditionCreateDialogOpen: false,
      categoryCreateDialogOpen: false,
      inventoryCreateFormValues: null,
      stockLineCreateDialogOpen: false,
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
      inventoryCopyFormValues: null,
      inventoryCopyDialogOpen: false,
      inventoryCopyProducts: [],
      initializedWithRecoverOrder: Boolean(props.recoverOrder),
      initializedWithRecoverOrderNotificationShown: false,
      freezedInitialValues: null,
      addedHCPCCodes: [],
    };

    this.onRequest = this.onRequest.bind(this);
    this.onInventoryScrollFetchRequest = this.onInventoryScrollFetchRequest.bind(this);
    this.onOrderLineAdd = this.onOrderLineAdd.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
    this.onInventoryCreateButtonClick = this.onInventoryCreateButtonClick.bind(this);
    this.onInventoryCreateDialogEntered = this.onInventoryCreateDialogEntered.bind(this);
    this.onInventoryCreateDialogClose = this.onInventoryCreateDialogClose.bind(this);
    this.onVendorCreate = this.onVendorCreate.bind(this);
    this.onVendorCreateDialogSubmitSuccess = this.onVendorCreateDialogSubmitSuccess.bind(this);
    this.onVendorCreateDialogClose = this.onVendorCreateDialogClose.bind(this);
    this.onWarehouseCreate = this.onWarehouseCreate.bind(this);
    this.onWarehouseCreateDialogSubmitSuccess = this.onWarehouseCreateDialogSubmitSuccess.bind(this);
    this.onWarehouseCreateDialogClose = this.onWarehouseCreateDialogClose.bind(this);
    this.onConditionCreate = this.onConditionCreate.bind(this);
    this.onConditionCreateDialogSubmitSuccess = this.onConditionCreateDialogSubmitSuccess.bind(this);
    this.onConditionCreateDialogClose = this.onConditionCreateDialogClose.bind(this);
    this.onCategoryCreate = this.onCategoryCreate.bind(this);
    this.onCategoryCreateDialogSubmitSuccess = this.onCategoryCreateDialogSubmitSuccess.bind(this);
    this.onCategoryCreateDialogClose = this.onCategoryCreateDialogClose.bind(this);
    this.onStockLineCreateButtonClick = this.onStockLineCreateButtonClick.bind(this);
    this.onStockLineCreateDialogClose = this.onStockLineCreateDialogClose.bind(this);
    this.onSelectedOrderLinesPrint = this.onSelectedOrderLinesPrint.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onStockLineCreateDialogCopyButtonClick =
      this.onStockLineCreateDialogCopyButtonClick.bind(this);
    this.onInventoryCopyDialogEntered = this.onInventoryCopyDialogEntered.bind(this);
    this.onInventoryCopyDialogClose = this.onInventoryCopyDialogClose.bind(this);
    this.onInventoryCopySubmitSuccess = this.onInventoryCopySubmitSuccess.bind(this);

    this.onRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    const { actions } = this.props;
    actions.clearConnectedOrders();
    actions.clearInventories();
  }

  componentDidMount() {
    const { Patient, variant, match, actions, account, history, location, archive } = this.props;
    actions.fetchFacilities({
      limit: 10,
      offset: 0,
      triggerLoading: true,
      recoverOrderValues: {
        [account.ID]: {
          'Patient ID': Patient ? Patient['Record Number'] : match.params.id,
        },
      },
    });
    actions.fetchSystemSettings({
      triggerLoading: false,
      recoverOrderValues: {
        [account.ID]: {
          'Patient ID': Patient ? Patient['Record Number'] : match.params.id,
        },
      },
    });
    actions.fetchBusiness({
      triggerLoading: false,
      recoverOrderValues: {
        [account.ID]: {
          'Patient ID': Patient ? Patient['Record Number'] : match.params.id,
        },
      },
    });
    if (variant === 'create' && !Patient) {
      actions.fetchPatient({
        'Record Number': match.params.id,
        triggerLoading: true,
        recoverOrderValues: {
          [account.ID]: {
            'Patient ID': match.params.id,
          },
        },
      });
    }
    if (variant === 'edit' || variant === 'copy') {
      if (location.state && location.state.fromCreate) {
        history.replace(location.pathname);
      }
      if (!location.state || !location.state.fromCreate) {
        if (archive) {
          actions.fetchArchiveOrder({
            ID: match.params.id,
            triggerLoading: true,
          });
        } else {
          actions.fetchOrder({
            ID: match.params.id,
            triggerLoading: true,
          });
        }
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      history,
      count,
      loading,
      loadingInventory,
      filters,
      connectedOrdersOrderID,
      actions,
      account,
      submitting,
      initialValues,
      variant,
    } = this.props;
    const {
      initializedWithRecoverOrder,
      initializedWithRecoverOrderNotificationShown,
      renderOrderLine,
    } = this.state;
    const filtersChanged =
      nextProps.filters['Product Name / HCPCS Code'] !== filters['Product Name / HCPCS Code']
      || nextProps.filters.Model !== filters.Model
      || nextProps.filters['Vendor Name'] !== filters['Vendor Name'];
    const newState = {};
    if (loading > 0 && nextProps.loading === 0 && !nextProps.Patient) {
      history.replace('/workflow/order/create/patient-assign');
    }
    if (filtersChanged) {
      newState.offset = 0;
    }
    if (count === 0) {
      newState.triggerLoading = true;
    } else {
      newState.triggerLoading = false;
    }
    if (nextProps.loadingInventory < loadingInventory) {
      newState.triggerLoading = false;
    }

    if (account.authenticated && !nextProps.account.authenticated) {
      newState.initializedWithRecoverOrder = false;
    }

    if (nextProps.submitting && !submitting) {
      newState.freezedInitialValues = initialValues;
    } else if (submitting && !nextProps.submitting) {
      newState.freezedInitialValues = null;
    }

    if (nextProps.connectedOrdersOrderID
      && nextProps.connectedOrdersOrderID !== connectedOrdersOrderID) {
      actions.clearConnectedOrders();
    }

    if (initializedWithRecoverOrder
      && !initializedWithRecoverOrderNotificationShown
      && nextProps.recoverOrder
      && nextProps.initialValues) {
      newState.initializedWithRecoverOrderNotificationShown = true;
      actions.addNotification({
        message: nextProps.initialValues.ID
          ? 'Order editing successfully recovered'
          : 'Order creation successfully recovered',
        variant: 'info',
      });
    }

    if (variant === 'edit'
      && !this.renderOrderLineInterval 
      && nextProps.initialValues && nextProps.initialValues.orderLine && nextProps.initialValues.orderLine.length > 0) {
      if (nextProps.initialValues.orderLine.length !== renderOrderLine.length) {
        this.setState({
          addedHCPCCodes: uniqBy([
            ...this.state.addedHCPCCodes,
            ...nextProps.order['Order Line'].map(orderLineItem => orderLineItem['HCPC S Code']),
          ], 'ID'),
        });
        const originalOrderLine = nextProps.initialValues.orderLine;
        this.renderOrderLineInterval = setInterval(() => {
          if (this.state.renderOrderLine.length === originalOrderLine.length) {
            clearInterval(this.renderOrderLineInterval);
            this.renderOrderLineInterval = null;    
          } else {
            this.setState({
              renderOrderLine: [
                ...originalOrderLine.slice(0, this.state.renderOrderLine.length + 1),
              ],
            });
          }
        }, 0);
      } else if (objectDiff(nextProps.initialValues.orderLine, renderOrderLine).length > 0) {
        this.setState({
          renderOrderLine: nextProps.initialValues.orderLine,
        });
      }
    }

    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const filtersChanged =
      prevProps.filters['Product Name / HCPCS Code'] !== filters['Product Name / HCPCS Code']
      || prevProps.filters.Model !== filters.Model
      || prevProps.filters['Vendor Name'] !== filters['Vendor Name'];
    if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    }
  }

  componentWillUnmount() {
    const { recoverOrder, initialValues, actions, account } = this.props;
    const { initializedWithRecoverOrder } = this.state;
    const timestamp = new Date().getTime();
    if (initializedWithRecoverOrder && recoverOrder && initialValues
      && (!recoverOrder.timestamp || Math.abs(timestamp - recoverOrder.timestamp) > 1000)) {
      actions.clearRecoverOrder(account.ID);
    }
  }

  onRequest() {
    const { actions, filters, formValues, account } = this.props;
    const { limit, offset, triggerLoading } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map((key) => {
        let name;
        let operation = 'startswith';
        if (key === 'Product Name / HCPCS Code') {
          if (/^[A-Z]+\d{1,4}$/i.test(filters[key])) {
            name = 'HCPC Codes';
          } else {
            name = 'Product Name';
            operation = 'contains';
          }
        } else {
          name = key;
        }
        if (name === 'Is Active') {
          operation = 'eq';
        }
        return {
          name,
          operation,
          val: filters[key],
          ...name !== 'Is Active' ? { case_sensitivity: false } : {},
        };
      })
      : null;
    if (requestFilters) {
      actions.fetchInventories({
        limit,
        offset,
        filters: requestFilters,
        sortBy: [{
          name: 'Total Quantity',
          sort: 'desc',
        }],
        triggerLoading,
        push: offset > 0,
        recoverOrderValues: {
          [account.ID]: formValues,
        },
      });
    } else {
      actions.clearInventories();
    }
  }

  onInventoryScrollFetchRequest() {
    const { offset, limit } = this.state;
    if ((this.props.inventory.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  onOrderLineAdd(values) {
    const { Patient, order, formValues, form, account, actions, business } = this.props;
    const lastStockLine = values['Last Arrival Stock Line'];
    const mainInsurance = formValues.Insurances ? formValues.Insurances[0] : null;
    const HCPCCodes = values['HCPC Codes'].reduce((accumulator, currentValue) => {
      let accumulatedValue;
      if (currentValue.length > 1) {
        accumulatedValue = currentValue.map((item, index) => ({
          ...item,
          'OR HCPC S Code': currentValue.map(groupItem => groupItem.ID),
        }));
      } else {
        accumulatedValue = currentValue;
      }
      return [
        ...accumulator.slice(0, accumulator.length),
        ...accumulatedValue,
      ];
    }, []).sort(a => (a['HCPC S Code'] === values['Main Hcpc Code']['HCPC S Code'] ? -1 : 1));
    const combinedDiagnoses = uniqBy([
      ...Patient.Diagnosis || [],
      ...order && order.Diagnosis ? order.Diagnosis : [],
    ], 'ID');
    const currentDate = moment().format(dateFormat);
    let priceState = Patient['Billing Address Information'].State;
    let priceIsRural = Patient['Billing Address Information']['Is Rural'];
    if (!priceState) {
      const selectedFacility = business.Facilities.find(facilitiesItem => facilitiesItem.ID === formValues['Facility ID']);
      priceState = selectedFacility && selectedFacility['Company Address'] ? selectedFacility['Company Address'].State : null;
      priceIsRural = selectedFacility && selectedFacility['Company Address'] ? selectedFacility['Company Address']['Is Rural'] : null;
      this.setState({
        addedHCPCCodes: uniqBy([
          ...this.state.addedHCPCCodes,
          ...HCPCCodes,
        ], 'ID'),
      });
    }
    if (!priceState) {
      priceState = business['Company Address'] ? business['Company Address'].State : null;
      priceIsRural = business['Company Address'] ? business['Company Address']['Is Rural'] : null;
    }
    actions.reset('inventorySearchForm');
    HCPCCodes.forEach((item) => {
      const medicaidPrice = item['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicaid'
        && priceItem.State === priceState);
      const medicarePrice = item['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicare'
        && priceItem.State === priceState);

      actions.arrayPush(form, 'orderLine', {
        'HCPC S Code': item['HCPC S Code'],
        'Product Name': values['Product Name'],
        ...mainInsurance && mainInsurance.profitPercentMedicaid && medicaidPrice && medicaidPrice.Description ? {
          Description: medicaidPrice.Description,
        } : {},
        ...mainInsurance && mainInsurance.profitPercentMedicare && medicarePrice && medicarePrice.Description ? {
          Description: medicarePrice.Description,
        } : {},
        ...!mainInsurance || (!mainInsurance.profitPercentMedicaid
          && !mainInsurance.profitPercentMedicare) ? {
            Description: (medicarePrice &&  medicarePrice.Description ? medicarePrice.Description : null)
              || (medicaidPrice && medicaidPrice.Description ? medicaidPrice.Description : null),
          } : {},
        ...medicaidPrice ? {
          'Medicaid Description': medicaidPrice.Description,
          'Medicaid Rental Price': priceIsRural ? medicaidPrice['Rental Price Rural'] : medicaidPrice['Rental Price'],
          'Medicaid Sale Price': priceIsRural ? medicaidPrice['Sale Price Rural'] : medicaidPrice['Sale Price'],
        } : {
          'Medicaid Description': '',
          'Medicaid Rental Price': '',
          'Medicaid Sale Price': '',
        },
        ...medicarePrice ? {
          'Medicare Description': medicarePrice.Description,
          'Medicare Rental Price': priceIsRural ? medicarePrice['Rental Price Rural'] : medicarePrice['Rental Price'],
          'Medicare Sale Price': priceIsRural ? medicarePrice['Sale Price Rural'] : medicarePrice['Sale Price'],
        } : {
          'Medicare Description': '',
          'Medicare Rental Price': '',
          'Medicare Sale Price': '',
        },
        ...lastStockLine && lastStockLine['Retail Price'] && lastStockLine['Is Retail Price For Claims'] ? {
          'Retail Price Claims': lastStockLine['Retail Price'],
          ...mainInsurance && mainInsurance.type !== 'Self' ? {
            'Charge': formatPrice(lastStockLine['Retail Price']),
            Description: values['Product Name'],
          } : {},
        } : {},
        ...lastStockLine && lastStockLine['Retail Price'] && lastStockLine['Is Retail Price For Self Pay'] ? {
          'Retail Price Self Pay': lastStockLine['Retail Price'],
          ...mainInsurance && mainInsurance.type === 'Self' ? {
            'Charge': formatPrice(lastStockLine['Retail Price']),
            Description: values['Product Name'],
          } : {},
        } : {},
        'HCPC S Code ID': item.ID,
        'Is Main HCPC S Code': item.ID === values['Main Hcpc Code'].ID,
        'Product ID': values.ID,
        assignedUserID: account.ID,
        assignedUserName: `${account['First Name']} ${account['Last Name']}`,
        assignedFromUserFirstName: account['First Name'],
        assignedFromUserLastName: account['Last Name'],
        'OR HCPC S Code': item['OR HCPC S Code'],
        ...(item.ID === values['Main Hcpc Code'].ID && lastStockLine && lastStockLine.Price)
          ? { Cost: formatPrice(lastStockLine.Price) }
          : { Cost: null },
        placeOfSRV: '12',
        dateOfServiceFrom: currentDate,
        dateOfServiceTo: currentDate,
        Units: 1,
        Diagnosis: formValues['Diagnosis ID']
          ? formValues['Diagnosis ID'].slice(0, 4).map((diagnosisID, index) => {
            const currentDiagnosis = combinedDiagnoses.filter(diagnosisItem =>
              diagnosisItem.ID === diagnosisID)[0];
            const diagnosisPointer = 'ABCDEFGHIJKL'.slice(index, index + 1);
            return {
              label: diagnosisPointer,
              value: currentDiagnosis.ID,
            };
          }) : [],
        Modifiers: item['Default Modifiers'],
        'Calories Per Unit': values['Calories Per Unit'],
      });
    });
    this.setState({
      inventoryCopyProducts: [
        ...this.state.inventoryCopyProducts,
        values,
      ],
    });
  }

  onSubmitButtonClick() {
    const { form, actions, selectedOrderLines } = this.props;
    selectedOrderLines.forEach((item) => {
      actions.change(form, `orderLine[${item}].Selected`, false);
    });
    setTimeout(() => {
      actions.submit(form);
    }, 200);
  }

  onInventoryCreateButtonClick() {
    this.setState({ inventoryCreateDialogOpen: true });
  }

  onInventoryCreateDialogEntered() {
    this.setState({
      inventoryCreateFormValues: null,
      inventoryCopyFormValues: null,
    });
  }

  onInventoryCreateDialogClose() {
    this.setState({ inventoryCreateDialogOpen: false });
  }

  onVendorCreate(variant) {
    this.setState({
      ...variant === 'create' && {
        inventoryCreateFormValues: this.props.inventoryCreateFormValues,
        inventoryCreateDialogOpen: false,
      },
      ...variant === 'copy' && {
        inventoryCopyFormValues: this.props.inventoryCopyFormValues,
        inventoryCopyDialogOpen: false,
      },
      vendorCreateDialogOpen: true,
    });
  }

  onVendorCreateDialogSubmitSuccess(values) {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      vendorCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
        inventoryCreateFormValues: {
          ...inventoryCreateFormValues,
          ...{
            'Vendor Name': values['Vendor Name'],
            'Vendor ID': values.ID,
          },
        },
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
        inventoryCopyFormValues: {
          ...inventoryCopyFormValues,
          ...{
            'Vendor Name': values['Vendor Name'],
            'Vendor ID': values.ID,
          },
        },
      },
    });
  }

  onVendorCreateDialogClose() {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      vendorCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
      },
    });
  }

  onWarehouseCreate(variant) {
    this.setState({
      ...variant === 'create' && {
        inventoryCreateFormValues: this.props.inventoryCreateFormValues,
        inventoryCreateDialogOpen: false,
      },
      ...variant === 'copy' && {
        inventoryCopyFormValues: this.props.inventoryCopyFormValues,
        inventoryCopyDialogOpen: false,
      },
      warehouseCreateDialogOpen: true,
    });
  }

  onWarehouseCreateDialogSubmitSuccess(values) {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      warehouseCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
        inventoryCreateFormValues: {
          ...inventoryCreateFormValues,
          ...{
            'Warehouse Location': values.Location,
            'Warehouse ID': values.ID,
          },
        },
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
        inventoryCopyFormValues: {
          ...inventoryCopyFormValues,
          ...{
            'Warehouse Location': values.Location,
            'Warehouse ID': values.ID,
          },
        },
      },
    });
  }

  onWarehouseCreateDialogClose() {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      warehouseCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
      },
    });
  }

  onCategoryCreate(variant) {
    this.setState({
      ...variant === 'create' && {
        inventoryCreateFormValues: this.props.inventoryCreateFormValues,
        inventoryCreateDialogOpen: false,
      },
      ...variant === 'copy' && {
        inventoryCopyFormValues: this.props.inventoryCopyFormValues,
        inventoryCopyDialogOpen: false,
      },
      categoryCreateDialogOpen: true,
    });
  }

  onCategoryCreateDialogSubmitSuccess(values) {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      categoryCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
        inventoryCreateFormValues: {
          ...inventoryCreateFormValues,
          ...{
            'Category Name': values.Name,
            'Category ID': values.ID,
          },
        },
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
        inventoryCopyFormValues: {
          ...inventoryCopyFormValues,
          ...{
            'Category Name': values.Name,
            'Category ID': values.ID,
          },
        },
      },
    });
  }

  onCategoryCreateDialogClose() {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      categoryCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
      },
    });
  }

  onConditionCreate(variant) {
    this.setState({
      ...variant === 'create' && {
        inventoryCreateFormValues: this.props.inventoryCreateFormValues,
        inventoryCreateDialogOpen: false,
      },
      ...variant === 'copy' && {
        inventoryCopyFormValues: this.props.inventoryCopyFormValues,
        inventoryCopyDialogOpen: false,
      },
      conditionCreateDialogOpen: true,
    });
  }

  onConditionCreateDialogSubmitSuccess(values) {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      conditionCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
        inventoryCreateFormValues: {
          ...inventoryCreateFormValues,
          ...{
            'Condition Name': values.Name,
            'Condition ID': values.ID,
          },
        },
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
        inventoryCopyFormValues: {
          ...inventoryCopyFormValues,
          ...{
            'Condition Name': values.Name,
            'Condition ID': values.ID,
          },
        },
      },
    });
  }

  onConditionCreateDialogClose() {
    const { inventoryCreateFormValues, inventoryCopyFormValues } = this.state;
    this.setState({
      conditionCreateDialogOpen: false,
      ...inventoryCreateFormValues && {
        inventoryCreateDialogOpen: true,
      },
      ...inventoryCopyFormValues && {
        inventoryCopyDialogOpen: true,
      },
    });
  }

  onStockLineCreateButtonClick() {
    this.setState({ stockLineCreateDialogOpen: true });
  }

  onStockLineCreateDialogClose() {
    this.setState({ stockLineCreateDialogOpen: false });
  }

  onSelectedOrderLinesPrint() {
    this.setState({ printDialogOpen: true });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onStockLineCreateDialogCopyButtonClick() {
    this.setState({
      stockLineCreateDialogOpen: false,
      inventoryCopyDialogOpen: true,
    });
  }

  onInventoryCopyDialogEntered() {
    this.setState({
      inventoryCreateFormValues: null,
      inventoryCopyFormValues: null,
    });
  }

  onInventoryCopyDialogClose() {
    this.setState({ inventoryCopyDialogOpen: false });
  }

  onInventoryCopySubmitSuccess(values) {
    const { form, formValues, error, actions } = this.props;
    const newOrderLine = formValues.orderLine.map((orderLine) => {
      if (orderLine['Product ID'] === error.params['Product ID']) {
        return {
          ...orderLine,
          'Product ID': values.ID,
          'Product Name': values['Product Name'],
        };
      }
      return orderLine;
    });
    actions.clearSubmitErrors(form);
    actions.change(form, 'orderLine', newOrderLine);
    this.setState({
      inventoryCopyProducts: [
        ...this.state.inventoryCopyProducts,
        values,
      ],
    });
  }

  render() {
    const {
      form,
      submitting,
      submitSucceeded,
      error,
      inventory,
      order,
      Patient,
      filters,
      loading,
      loadingInventory,
      orderLines,
      selectedOrderLines,
      variant,
      actions,
      formValues,
      history,
      systemSettings,
      business,
      archive,
      pristine,
      classes,
    } = this.props;
    const {
      inventoryCreateDialogOpen,
      vendorCreateDialogOpen,
      warehouseCreateDialogOpen,
      categoryCreateDialogOpen,
      conditionCreateDialogOpen,
      inventoryCreateFormValues,
      stockLineCreateDialogOpen,
      printDialogOpen,
      signDialogOpen,
      printFormValues,
      inventoryCopyDialogOpen,
      inventoryCopyProducts,
      inventoryCopyFormValues,
      renderOrderLine,
      addedHCPCCodes,
    } = this.state;
    const initialValues = this.state.freezedInitialValues || this.props.initialValues;
    const ordersActionsBarActive = selectedOrderLines.length > 0;
    const diffValues = objectDiff({
      ...formValues,
      ...formValues.orderLine ? { orderLine: formValues.orderLine
        .map(({ Selected, ...otherOrderLineValues }) => otherOrderLineValues),
      } : {},
    }, initialValues);
    const inventoryCopyProduct = (error && error.params && error.params['Product ID'])
      ? inventoryCopyProducts.find(item => item.ID === error.params['Product ID'])
      : {};
    const errorProduct = error && error.params && error.params['Product ID']
      ? formValues.orderLine.filter(item => item['Product ID'] === error.params['Product ID'])[0]
      : null;
    return (
      <Layout>
        {(!Patient || loading > 0) ? (
          <Progress />
        ) : (
          <Fragment>
            <Helmet>
              <title>
                {variant === 'edit'
                  ? `${appTitle} ${titleSeparator} Edit ${formatOrderNumber(order.ID)}`
                  : `${appTitle} ${titleSeparator} Create Order`}
              </title>
            </Helmet>
            <PatientDetailsBar
              form={form}
              variant={variant === 'edit' ? 'order-edit' : 'order-create'}
              patient={Patient}
            />
            <Toolbar
              className={classes.toolbar}
              disableGutters
            >
              <Grid
                container
                justify="flex-end"
                alignItems="center"
                spacing={24}
                wrap="nowrap"
              >
                <Grid className={classes.gridItemShrink} item xs={12}>
                  <InventorySearch
                    inventory={inventory}
                    loading={loadingInventory}
                    filters={filters}
                    onScrollFetchRequest={this.onInventoryScrollFetchRequest}
                    onResultsItemClick={this.onOrderLineAdd}
                    onInventoryCreateButtonClick={this.onInventoryCreateButtonClick}
                    onCancelButtonClick={() => {
                      window.close();
                    }}
                    cancelButtonText="Close"
                    onSubmitButtonClick={this.onSubmitButtonClick}
                    searchDisabled={submitting || archive}
                    submitButtonDisabled={submitting || archive || pristine}
                    submitButtonLoading={submitting}
                    {...variant === 'create' ? {
                      submitButtonText: 'Done',
                    } : {}}
                    {...variant === 'edit' || variant === 'copy' ? {
                      submitButtonText: 'Save',
                    } : {}}
                    {...variant === 'view' ? {
                      submitButtonText: 'Print',
                    } : {}}
                  />
                </Grid>
              </Grid>
            </Toolbar>
            <div>
              <OrderCreateEditForm
                variant={variant}
                form={form}
                formValues={formValues}
                initialValues={{
                  ...initialValues,
                  ...(variant === 'edit' && renderOrderLine) ? {
                    orderLine: renderOrderLine,
                  } : {},
                }}
                lazyInitialized={variant === 'edit' && renderOrderLine
                  ? renderOrderLine.length === initialValues.orderLine.length : true}
                keepDirtyOnReinitialize
                onSubmitSuccess={(values, dispatch, props) => {
                  if (window.opener && window.opener.postMessage) {
                    window.opener.postMessage({
                      type: 'order-update-success',
                      values,
                    });
                  }
                  actions.addNotification({
                    message: variant === 'edit'
                      ? 'Order successfully updated'
                      : 'New order successfully added',
                    variant: 'success',
                  });
                  if (variant === 'edit') {
                    props.reset();
                  }
                  if (variant === 'create' || variant === 'copy') {
                    history.replace({
                      pathname: `/workflow/order/edit/${values.ID}`,
                      state: {
                        fromCreate: true,
                      },
                    });
                  }
                }}
                Patient={Patient}
                order={order}
                onStockLineCreateButtonClick={this.onStockLineCreateButtonClick}
                syncUpdates={selectedOrderLines.length > 0}
                systemSettings={systemSettings}
                business={business}
                disabled={archive}
                addedHCPCCodes={addedHCPCCodes}
              />
              <Collapse
                in={ordersActionsBarActive}
                direction="up"
                mountOnEnter
                unmountOnExit
              >
                <div className={classes.gutterBottom} role="presentation" />
              </Collapse>
            </div>
            <OrdersActionsBar
              open={ordersActionsBarActive}
              count={selectedOrderLines.length}
              totalCount={orderLines.length}
              {...variant === 'edit' ? {
                selectedOrderLines: selectedOrderLines
                  ? order['Order Line']
                    .filter((item, index) => selectedOrderLines.indexOf(index) !== -1)
                  : [],
              } : {}}
              onPrint={this.onSelectedOrderLinesPrint}
              onSelect={() => {
                orderLines.forEach((item) => {
                  actions.change(form, `orderLine[${item}].Selected`, true);
                });
              }}
              onDeselect={() => {
                selectedOrderLines.forEach((item) => {
                  actions.change(form, `orderLine[${item}].Selected`, false);
                });
              }}
              onRemove={() => {
                selectedOrderLines
                  .sort()
                  .reverse()
                  .forEach((item) => {
                    actions.arrayRemove(form, 'orderLine', item);
                  });
              }}
              {...variant === 'edit' && {
                onSubmitSuccess: (values) => {
                  const newOrderLine = formValues.orderLine.map((item) => {
                    const newItem = values.find(orderLine => orderLine.ID === item.ID);
                    if (newItem) {
                      return {
                        ...item,
                        Status: newItem['Workflow Status'].map(statusItem => ({
                          value: statusItem.ID,
                          label: statusItem.Name,
                        })),
                        'Assigned User ID': newItem['Assigned User'].ID,
                        ...newItem.Driver && newItem.Driver.ID
                          ? {
                            driverID: newItem.Driver.ID,
                            driverName: `${newItem.Driver['First Name']} ${newItem.Driver['Last Name']}`,
                          } : {},
                        ...newItem['Date of Delivery']
                          ? {
                            dateOfDelivery: moment(newItem['Date of Delivery'], timeDateFormat).format(dateFormat),
                            timeOfDelivery: moment(newItem['Date of Delivery'], timeDateFormat).format(timeFormat),
                          } : {},
                      };
                    }
                    return item;
                  });
                  actions.change(form, 'orderLine', newOrderLine);
                },
              }}
              variant={variant}
              archive={archive}
            />
            {variant === 'edit' &&
              <Fragment>
                <PrintDialog
                  form={`${order.ID}-orderPrint-selectedOrderLines`}
                  variant="order"
                  open={printDialogOpen}
                  onClose={this.onPrintDialogClose}
                  onSign={this.onPrintDialogSign}
                  ID={order.ID}
                  patient={order.Patient}
                  prescriber={order.Prescriber}
                  facility={order.Facility}
                  business={business}
                  systemSettings={systemSettings}
                  diagnosis={order.Diagnosis}
                  {...{
                    'Order Line': order['Order Line'].filter((item, index) =>
                      selectedOrderLines.indexOf(index) !== -1),
                    'Primary Insurance': order['Primary Insurance'],
                    'Order Insurance Param': order['Order Insurance Param'],
                  }}
                />
                <DocumentsSignDialog
                  open={signDialogOpen}
                  onClose={this.onSignDialogClose}
                  printFormValues={printFormValues}
                  variant="order"
                  ID={order.ID}
                  patient={order.Patient}
                  prescriber={order.Prescriber}
                  {...{
                    'Order Line': order['Order Line'].filter((item, index) =>
                      selectedOrderLines.indexOf(index) !== -1),
                    'Primary Insurance': order['Primary Insurance'],
                    'Order Insurance Param': order['Order Insurance Param'],
                  }}
                />
              </Fragment>
            }
            <Prompt
              when={Object.keys(diffValues).length > 0 && !submitSucceeded}
              message="All changes will be lost. Are you sure that you want to leave this page?"
            />
            <InventoryCreateEditDialog
              open={inventoryCreateDialogOpen}
              onEntered={this.onInventoryCreateDialogEntered}
              onClose={this.onInventoryCreateDialogClose}
              onVendorCreate={() => { this.onVendorCreate('create'); }}
              onCategoryCreate={() => { this.onCategoryCreate('create'); }}
              onConditionCreate={() => { this.onConditionCreate('create'); }}
              onWarehouseCreate={() => { this.onWarehouseCreate('create'); }}
              form="inventoryCreate"
              {...inventoryCreateFormValues
                ? { initialValues: inventoryCreateFormValues } : {}}
              onSubmitSuccess={this.onOrderLineAdd}
            />
            <VendorCreateEditDialog
              form="vendorCreate"
              open={vendorCreateDialogOpen}
              onClose={this.onVendorCreateDialogClose}
              onSubmitSuccess={this.onVendorCreateDialogSubmitSuccess}
            />
            <InventoryWarehouseCreateEditDialog
              form="warehouseCreate"
              open={warehouseCreateDialogOpen}
              onClose={this.onWarehouseCreateDialogClose}
              onSubmitSuccess={this.onWarehouseCreateDialogSubmitSuccess}
            />
            <InventoryCategoryCreateEditDialog
              form="categoryCreate"
              open={categoryCreateDialogOpen}
              onClose={this.onCategoryCreateDialogClose}
              onSubmitSuccess={this.onCategoryCreateDialogSubmitSuccess}
            />
            <InventoryConditionCreateEditDialog
              form="conditionCreate"
              open={conditionCreateDialogOpen}
              onClose={this.onConditionCreateDialogClose}
              onSubmitSuccess={this.onConditionCreateDialogSubmitSuccess}
            />
            {(error && error.params && error.params['Product ID']) &&
              <Fragment>
                <InventoryCreateEditDialog
                  open={stockLineCreateDialogOpen}
                  onClose={this.onStockLineCreateDialogClose}
                  onCopyButtonClick={this.onStockLineCreateDialogCopyButtonClick}
                  form={`${error.params['Product ID']}-inventoryStockLineCreate`}
                  {...{ 'Product ID': error.params['Product ID'] }}
                  initialValues={{
                    'Units Quantity': errorProduct && errorProduct.Units
                      ? errorProduct.Units : '',
                    'Price per Unit': errorProduct && errorProduct.Cost
                      ? errorProduct.Cost : '',
                  }}
                  onSubmitSuccess={() => {
                    actions.clearSubmitErrors(form);
                  }}
                />
                <InventoryCreateEditDialog
                  open={inventoryCopyDialogOpen}
                  onEntered={this.onInventoryCreateDialogEntered}
                  onClose={this.onInventoryCopyDialogClose}
                  onVendorCreate={() => { this.onVendorCreate('copy'); }}
                  onCategoryCreate={() => { this.onCategoryCreate('copy'); }}
                  onConditionCreate={() => { this.onConditionCreate('copy'); }}
                  onWarehouseCreate={() => { this.onWarehouseCreate('copy'); }}
                  form="inventoryCopy"
                  variant="copy-add"
                  initialValues={inventoryCopyFormValues
                    || {
                      'Product Name': inventoryCopyProduct['Product Name'] || '',
                      'Vendor Name': inventoryCopyProduct.Vendor['Vendor Name'] || '',
                      'Vendor ID': inventoryCopyProduct.Vendor.ID || '',
                      Model: inventoryCopyProduct.Model || '',
                      'HCPC Codes': inventoryCopyProduct['HCPC Codes']
                        ? inventoryCopyProduct['HCPC Codes'].map(item => (
                          item.map(nestedItem => nestedItem['HCPC S Code'])
                        )) : [],
                      'Do not override': false,
                      'Supply Type': 'Units',
                    }
                  }
                  onSubmitSuccess={this.onInventoryCopySubmitSuccess}
                />
              </Fragment>
            }
          </Fragment>
        )}
      </Layout>
    );
  }
}

OrderCreateEdit.propTypes = {
  form: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  order: PropTypes.shape({
    ID: PropTypes.number,
  }),
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    'Insurance Information': PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
  }),
  inventory: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  loadingInventory: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Product Name / HCPCS Code': PropTypes.string,
    'Vendor Name': PropTypes.string,
    Model: PropTypes.string,
  }).isRequired,
  orderLines: PropTypes.arrayOf(PropTypes.number).isRequired,
  selectedOrderLines: PropTypes.arrayOf(PropTypes.number).isRequired,
  connectedOrdersOrderID: PropTypes.number,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  facilities: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['create', 'edit', 'copy']),
  inventoryCreateFormValues: PropTypes.shape({
    'Product Name': PropTypes.string,
    Model: PropTypes.string,
  }),
  inventoryCopyFormValues: PropTypes.shape({
    'Product Name': PropTypes.string,
  }),
  actions: PropTypes.shape({
    fetchPatient: PropTypes.func.isRequired,
    fetchOrder: PropTypes.func.isRequired,
    clearOrders: PropTypes.func.isRequired,
    clearInventories: PropTypes.func.isRequired,
    fetchInventories: PropTypes.func.isRequired,
    clearConnectedOrders: PropTypes.func.isRequired,
    fetchFacilities: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    arrayPush: PropTypes.func.isRequired,
    arrayRemove: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    clearRecoverOrder: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  account: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    authenticated: PropTypes.bool,
  }).isRequired,
  formValues: PropTypes.shape({
    'Transmission Method': PropTypes.string,
  }),
  initialValues: PropTypes.shape({
    'Transmission Method': PropTypes.string,
    ID: PropTypes.number,
  }),
  recoverOrder: PropTypes.shape({
    'Patient ID': PropTypes.number.isRequired,
  }),
  classes: PropTypes.shape({
    gutterBottom: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

OrderCreateEdit.defaultProps = {
  form: 'orderCreate',
  order: null,
  Patient: null,
  variant: 'create',
  error: null,
  connectedOrdersOrderID: null,
  inventoryCreateFormValues: {},
  inventoryCopyFormValues: null,
  formValues: {},
  initialValues: {},
  business: null,
  recoverOrder: null,
};

const mapStateToProps = (store, ownProps) => {
  const inventorySearchFormValues = getFormValues('inventorySearchForm')(store);
  const inventoryCreateFormValues = getFormValues('inventoryCreate')(store);
  const inventoryCopyFormValues = getFormValues('inventoryCopy')(store);
  const formValues = getFormValues(ownProps.form)(store);
  let order = null;
  let Patient;
  let primaryInsuranceParams;
  let patientInsurances;
  if (ownProps.variant === 'create') {
    Patient = store.patients.data.length > 0
      ? store.patients.data.find(patient => patient['Record Number'] === Number(ownProps.match.params.id))
      : null;
    patientInsurances = Patient ? sortInsurance(Patient['Insurance Information'])
      .filter(item => item['Insurance Status'] === 'Active') : [];
  } else if (ownProps.variant === 'edit' || ownProps.variant === 'copy') {
    if (ownProps.archive) {
      order = store.archiveOrders.data.length > 0 && !store.form.ordersFilter
        ? store.archiveOrders.data.find(order => order.ID === Number(ownProps.match.params.id))
        : null;
    } else {
      order = store.orders.data.length > 0 && !store.form.ordersFilter
      ? store.orders.data.find(item => item.ID === Number(ownProps.match.params.id))
      : null;
    }
    // !store.form.ordersFilter - check if order lines are not filtered by State
    Patient = order ? order.Patient : null;
    primaryInsuranceParams = order
      ? order['Order Insurance Param'].find(item =>
        item.Insurance.ID === order['Primary Insurance'].ID)
      : null;
  }
  return {
    order,
    Patient,
    inventory: store.inventory.data,
    count: store.inventory.count,
    loadingInventory: store.inventory.loading,
    loading: store.orders.loading
      + store.archiveOrders.loading
      + store.patients.loading
      + store.facilities.loading,
    connectedOrdersOrderID: store.connectedOrders.orderID,
    facilities: store.facilities.data,
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    recoverOrder: store.recoverOrder
      ? store.recoverOrder[store.account.ID]
      : null,
    filters: {
      ...inventorySearchFormValues
        && inventorySearchFormValues['Product Name / HCPCS Code']
        ? { 'Product Name / HCPCS Code': inventorySearchFormValues['Product Name / HCPCS Code'] }
        : {},
      ...inventorySearchFormValues
        && inventorySearchFormValues['Vendor Name']
        && inventorySearchFormValues['Vendor Name'].length >= 3
        ? { 'Vendor Name': inventorySearchFormValues['Vendor Name'] }
        : {},
      ...inventorySearchFormValues
        && inventorySearchFormValues.Model
        ? { Model: inventorySearchFormValues.Model }
        : {},
      'Is Active': true,
    },
    ...(Patient && store.facilities.data.length > 0
      && store.recoverOrder
      && store.recoverOrder[store.account.ID]) ? {
        initialValues: store.recoverOrder[store.account.ID],
      } : {},
    ...(ownProps.variant === 'create'
      && Patient && store.facilities.data.length > 0
      && store.systemSettings.data['System Config']
      && !store.recoverOrder[store.account.ID]) ? {
        initialValues: {
          'Patient ID': Patient['Record Number'],
          ...store.form[ownProps.form]
            && store.form[ownProps.form].initial
            && store.form[ownProps.form].initial['Vitals ID']
            ? { 'Vitals ID': store.form[ownProps.form].initial['Vitals ID'] }
            : { 'Vitals ID': Patient.Vitals.map(item => item.ID).sort((a, b) => b - a).slice(0, 1) },
          ...store.form[ownProps.form]
            && store.form[ownProps.form].initial
            && store.form[ownProps.form].initial['Prescriber ID']
            ? {
              'Prescriber ID': store.form[ownProps.form].initial['Prescriber ID'],
              'Prescriber Name': store.form[ownProps.form].initial['Prescriber Name'],
              'Selected Prescriber Name': store.form[ownProps.form].initial['Selected Prescriber Name'],
            }
            : {
              ...Patient.Prescriber.length > 0
                ? {
                  'Prescriber ID': Patient.Prescriber[0].ID,
                  'Prescriber Name': formatPrescriberName(Patient.Prescriber[0]),
                  'Selected Prescriber Name': formatPrescriberName(Patient.Prescriber[0]),
                }
                : {},
            },
          ...store.form[ownProps.form]
            && store.form[ownProps.form].initial
            && store.form[ownProps.form].initial['Diagnosis ID']
            ? { 'Diagnosis ID': store.form[ownProps.form].initial['Diagnosis ID'] }
            : {
              ...Patient.Diagnosis.length > 0
                ? { 'Diagnosis ID': Patient.Diagnosis.map(item => item.ID).slice(0, 12) }
                : {},
            },
          ...store.form[ownProps.form]
            && store.form[ownProps.form].initial
            && store.form[ownProps.form].initial['Referral Source ID']
            ? {
              'Referral Source ID': store.form[ownProps.form].initial['Referral Source ID'],
              'Referral Source Name': store.form[ownProps.form].initial['Referral Source Name'],
              'Selected Referral Source Name': store.form[ownProps.form].initial['Selected Referral Source Name'],
            }
            : {
              ...Patient['Referral Source'].length > 0
                ? {
                  'Referral Source ID': Patient['Referral Source'][0].ID,
                  'Referral Source Name': Patient['Referral Source'][0]['Name of source'],
                  'Selected Referral Source Name': Patient['Referral Source'][0]['Name of source'],
                }
                : {},
            },
          ...store.form[ownProps.form]
            && store.form[ownProps.form].initial
            && store.form[ownProps.form].initial['Sales Representative ID']
            ? { 'Sales Representative ID': store.form[ownProps.form].initial['Sales Representative ID'] }
            : {
              ...Patient['Sales Representative'].length > 0
                ? { 'Sales Representative ID': [Patient['Sales Representative'][0].ID] }
                : {},
            },
          ...patientInsurances.length > 0
            && !(patientInsurances.length === 1 && patientInsurances[0]['Insurance Type'] === 'Self')
            ? {
              Insurances: [{
                value: patientInsurances[0].ID,
                label: patientInsurances[0]['Insurance Info']['Payer Name'],
                type: patientInsurances[0]['Insurance Type'],
                ...patientInsurances[0]['Order Benefits'] === 'Primary' ? { startBadge: '1' } : {},
                ...patientInsurances[0]['Order Benefits'] === 'Secondary' ? { startBadge: '2' } : {},
                ...patientInsurances[0]['Order Benefits'] === 'Secondary As Primary' ? { startBadge: '2' } : {},
                ...patientInsurances[0]['Order Benefits'] === 'Tertiary' ? { startBadge: '3' } : {},
                profitPercentMedicaid: patientInsurances[0]['Insurance Info']['Profit Percent Medicaid'],
                profitPercentMedicare: patientInsurances[0]['Insurance Info']['Profit Percent Medicare'],
                profitPercentDefault: store.systemSettings.data['System Config'].PROFIT_PERCENT,
              }],
              'Transmission Method': patientInsurances[0]['Transmission Method'],
            }
            : {
              Insurances: [],
            },
          ...store.facilities.data.length > 0
            ? {
              'Facility ID': store.facilities.data[0].ID,
              'Company Name': store.facilities.data[0]['Company Info']['Company Name'],
              'Selected Company Name': store.facilities.data[0]['Company Info']['Company Name'],
            }
            : {},
          'Order Insurance Param': [],
        } } : {},
    ...((ownProps.variant === 'edit' || ownProps.variant === 'copy')
      && order && !store.recoverOrder[store.account.ID]) ? {
        initialValues: {
          ...ownProps.variant !== 'copy' ? { ID: order.ID } : {},
          'Patient ID': order.Patient['Record Number'],
          'Facility ID': order.Facility.ID,
          'Company Name': order.Facility['Company Info']['Company Name'],
          'Selected Company Name': order.Facility['Company Info']['Company Name'],
          'Transmission Method': order['Transmission Method'],
          Insurances: order.Insurance.length > 0
            ? uniqBy([
              order['Primary Insurance'],
              ...order.Insurance,
            ], 'ID')
              .filter(item => item.ID)
              .map(item => ({
                value: item.ID,
                label: item['Insurance Type'] === 'Self'
                  ? 'Self Payer'
                  : item['Insurance Info']['Payer Name'],
                ...item['Insurance Type'] === 'Self' ? { type: 'Self' } : { type: 'Normal' },
                ...item['Order Benefits'] === 'Primary' ? { startBadge: '1' } : {},
                ...item['Order Benefits'] === 'Secondary' ? { startBadge: '2' } : {},
                ...item['Order Benefits'] === 'Secondary As Primary' ? { startBadge: '2' } : {},
                ...item['Order Benefits'] === 'Tertiary' ? { startBadge: '3' } : {},
                profitPercentMedicaid: item['Insurance Info']['Profit Percent Medicaid'],
                profitPercentMedicare: item['Insurance Info']['Profit Percent Medicare'],
                profitPercentDefault: store.systemSettings.data['System Config'].PROFIT_PERCENT,
              }))
            : [{
              value: null,
              label: 'Self Payer',
            }],
          'Order Insurance Param': order['Order Insurance Param'].map((item) => ({
            Authorization: item.Authorization,
            'Authorization From': item['Authorization From'],
            'Authorization To': item['Authorization To'],
            ...ownProps.variant !== 'copy' ? { ID: item.ID } : {},
            'Insurance ID': item.Insurance.ID,
            'Original Ref Number': item['Original Ref Number'],
            Resubmission: item.Resubmission,
          })),
          Authorization: primaryInsuranceParams ? primaryInsuranceParams.Authorization : null,
          'Authorization From': primaryInsuranceParams ? primaryInsuranceParams['Authorization From'] : null,
          'Authorization To': primaryInsuranceParams ? primaryInsuranceParams['Authorization To'] : null,
          Resubmission: primaryInsuranceParams ? primaryInsuranceParams.Resubmission : null,
          'Original Ref Number': primaryInsuranceParams ? primaryInsuranceParams['Original Ref Number'] : null,
          ...order.Prescriber.ID ? {
            'Prescriber ID': order.Prescriber.ID,
            'Prescriber Name': formatPrescriberName(order.Prescriber),
            'Selected Prescriber Name': formatPrescriberName(order.Prescriber),
          } : {},
          'Referral Source ID': order['Referral Source'].ID,
          'Referral Source Name': order['Referral Source']['Name of source'],
          'Selected Referral Source Name': order['Referral Source']['Name of source'],
          'Vitals ID': order.Vitals.map(item => item.ID),
          'Diagnosis ID': order.Diagnosis.map(item => item.ID),
          'Sales Representative ID': order['Sales Representative'].map(item => item.ID),
          orderLine: order['Order Line'].map((item, index) => {
            const itemHCPCSCodesGroup = item.Product['HCPC Codes'].find(group => group.findIndex(groupItem => groupItem.ID === item['HCPC S Code'].ID) !== -1);
            let orHCPCSCode;
            if (itemHCPCSCodesGroup && itemHCPCSCodesGroup.length > 1) {
              orHCPCSCode = itemHCPCSCodesGroup.map(groupItem => groupItem.ID);
            }

            let priceState = Patient['Billing Address Information'].State;
            let priceIsRural = Patient['Billing Address Information']['Is Rural'];
            if (!priceState) {
              priceState = order.Facility['Company Address'] ? order.Facility['Company Address'].State : null;
              priceIsRural = order.Facility['Company Address'] ? order.Facility['Company Address']['Is Rural'] : null;
            }
            if (!priceState) {
              priceState = store.business.data['Company Address'] ? store.business.data['Company Address'].State : null;
              priceIsRural = store.business.data['Company Address'] ? store.business.data['Company Address']['Is Rural'] : null;
            }

            const medicaidPrice = item['HCPC S Code']['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicaid'
              && priceItem.State === priceState);
            const medicarePrice = item['HCPC S Code']['HCPC S Code Price'].find(priceItem => priceItem['Price Type'] === 'Medicare'
              && priceItem.State === priceState);

            return {
              ...ownProps.variant !== 'copy' ? { ID: item.ID } : {},
              Status: item['Workflow Status'].map(statusItem => ({
                value: statusItem.ID,
                label: statusItem.Name,
              })),
              ...item.Narrative ? { Narrative: item.Narrative } : {},
              Type: item.Type,
              dateOfServiceFrom: item['Date of Service From'],
              dateOfServiceTo: item['Date of Service To'],
              Description: item.Description,
              Units: item.Product['Calories Per Unit'] ? Math.floor(item.Units * 100 / item.Product['Calories Per Unit']) : item.Units.toString(),
              Charge: formatPrice(item.Charge),
              'CMN/AMB': item['CMN/AMB'],
              ...item.Cost ? { Cost: formatPrice(item.Cost) } : {},
              placeOfSRV: item['Place of SRV'].toString(),
              ...item.Refill || item.Refill === 0 ? { Refill: item.Refill.toString() } : {},
              ...item.Reimbs && item.Reimbs !== 'None' ? { Reimbs: item.Reimbs } : {},
              assignedUserID: item['Assigned User'].ID,
              assignedUserName: `${item['Assigned User']['First Name']} ${item['Assigned User']['Last Name']}`,
              assignedFromUserFirstName: item['Assigned From User']['First Name'],
              assignedFromUserLastName: item['Assigned From User']['Last Name'],
              ...item.Driver && item.Driver.ID ? { driverID: item.Driver.ID } : {},
              ...item.Driver && item.Driver.ID ? { driverName: `${item.Driver['First Name']} ${item.Driver['Last Name']}` } : {},
              ...item['Date of Delivery'] ? {
                dateOfDelivery: moment(item['Date of Delivery'], timeDateFormat).format(dateFormat),
                timeOfDelivery: moment(item['Date of Delivery'], timeDateFormat).format(timeFormat),
              } : {},
              ...item.Modifiers ? {
                Modifiers: ['', '', '', ''].map((modifiersItem, index) =>
                  item.Modifiers.slice(index * 2, (index * 2) + 2)),
              } : { },
              'HCPC S Code': item['HCPC S Code']['HCPC S Code'],
              'HCPC S Code ID': item['HCPC S Code'].ID,
              'Is Main HCPC S Code': item['HCPC S Code'].ID === item.Product['Main Hcpc Code'].ID,
              ...orHCPCSCode ? { 'OR HCPC S Code': orHCPCSCode } : {},
              'Product ID': item.Product.ID,
              'Product Name': item.Product['Product Name'],
              ...medicaidPrice ? {
                'Medicaid Description': medicaidPrice.Description,
                'Medicaid Rental Price': priceIsRural ? medicaidPrice['Rental Price Rural'] : medicaidPrice['Rental Price'],
                'Medicaid Sale Price': priceIsRural ? medicaidPrice['Sale Price Rural'] : medicaidPrice['Sale Price'],
              } : {
                'Medicaid Description': '',
                'Medicaid Rental Price': '',
                'Medicaid Sale Price': '',
              },
              ...medicarePrice ? {
                'Medicare Description': medicarePrice.Description,
                'Medicare Rental Price': priceIsRural ? medicarePrice['Rental Price Rural'] : medicarePrice['Rental Price'],
                'Medicare Sale Price': priceIsRural ? medicarePrice['Sale Price Rural'] : medicarePrice['Sale Price'],
              } : {
                'Medicare Description': '',
                'Medicare Rental Price': '',
                'Medicare Sale Price': '',
              },
              'Split for Claim': item['Split for Claim'],
              'Is Rejected': item['Is Rejected'],
              'Calories Per Unit': item.Product['Calories Per Unit'],
              Diagnosis: uniqBy([
                ...item['Primary Diagnosis'],
                ...item.Diagnosis,
              ], 'ID').map((diagnosisItem) => {
                const diagnosisIndex = order.Diagnosis.findIndex(orderDiagnosisItem =>
                  orderDiagnosisItem.ID === diagnosisItem.ID);
                const diagnosisPointer = 'ABCDEFGHIJKL'.slice(diagnosisIndex, diagnosisIndex + 1);
                return {
                  value: diagnosisItem.ID,
                  label: diagnosisPointer,
                  ...item['Primary Diagnosis'][0]
                    && diagnosisItem.ID === item['Primary Diagnosis'][0].ID ? { main: true } : {},
                };
              }),
            };
          }),
        } } : {},
    orderLines: formValues
      && formValues.orderLine
      ? formValues.orderLine.map((item, index) => index)
      : [],
    selectedOrderLines: formValues
      && formValues.orderLine
      ? formValues.orderLine.reduce((accumulator, currentValue, currentIndex) => {
        if (currentValue.Selected) {
          return [
            ...accumulator.slice(0, accumulator.length),
            currentIndex,
          ];
        }
        return accumulator;
      }, [])
      : [],
    inventoryCreateFormValues,
    inventoryCopyFormValues,
    formValues,
    error: getFormError(ownProps.form)(store),
    submitting: isSubmitting(ownProps.form)(store),
    pristine: isPristine(ownProps.form)(store),
    submitSucceeded: hasSubmitSucceeded(ownProps.form)(store),
    account: store.account,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatient,
    fetchOrder,
    clearOrders,
    fetchArchiveOrder,
    clearInventories,
    fetchInventories,
    clearConnectedOrders,
    fetchFacilities,
    submit,
    reset,
    change,
    arrayPush,
    arrayRemove,
    clearSubmitErrors,
    fetchSystemSettings,
    fetchBusiness,
    clearRecoverOrder,
    addNotification,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(OrderCreateEdit),
);



// WEBPACK FOOTER //
// ./src/pages/workflow/OrderCreateEdit.js